import type { IPFamily, IPInfo } from '../shared/types'

export async function fetchInfo(): Promise<IPInfo> {
	const res = await fetch('/json', { headers: { Accept: 'application/json' } })
	if (!res.ok) throw new Error(`HTTP ${res.status}`)
	return res.json()
}

/**
 * Cloudflare only ever sees the family the browser chose, so the other one comes from
 * icanhazip's single-stack hosts. Returns null if that family isn't reachable.
 */
export async function fetchOtherFamily(have: IPFamily): Promise<string | null> {
	const url = have === 'ipv4' ? 'https://ipv6.icanhazip.com' : 'https://ipv4.icanhazip.com'
	try {
		const res = await fetch(url, { signal: AbortSignal.timeout(4000) })
		if (!res.ok) return null
		const ip = (await res.text()).trim()
		const ok = have === 'ipv4' ? ip.includes(':') : /^\d+\.\d+\.\d+\.\d+$/.test(ip)
		return ok ? ip : null
	} catch {
		return null
	}
}

/** Best of N round trips to /ping, in milliseconds. */
export async function measurePing(n = 5): Promise<number> {
	let best = Infinity
	for (let i = 0; i < n; i++) {
		const t = performance.now()
		await fetch('/ping', { cache: 'no-store' })
		best = Math.min(best, performance.now() - t)
	}
	return Math.round(best)
}

export interface SpeedResult {
	mbps: number
	seconds: number
	bytes: number
	pingMs: number
}

/** Download a burst of random bytes from the edge and time it. */
export async function speedTest(bytes = 8 * 1024 * 1024, onProgress?: (fraction: number, mbpsSoFar: number) => void): Promise<SpeedResult> {
	const pingMs = await measurePing(4)
	const start = performance.now()
	const res = await fetch(`/speed?bytes=${bytes}`, { cache: 'no-store' })
	if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)
	const reader = res.body.getReader()
	let received = 0
	for (;;) {
		const { done, value } = await reader.read()
		if (done) break
		received += value.byteLength
		const secs = (performance.now() - start) / 1000
		onProgress?.(received / bytes, (received * 8) / secs / 1e6)
	}
	const seconds = (performance.now() - start) / 1000
	return { mbps: Math.round(((received * 8) / seconds / 1e6) * 10) / 10, seconds, bytes: received, pingMs }
}

export async function fetchHaiku(): Promise<{ haiku: string; model: string }> {
	const res = await fetch('/haiku')
	if (!res.ok) throw new Error(`HTTP ${res.status}`)
	return res.json()
}
